import React, { useCallback, useEffect } from 'react';

import { Box, Card, Flex, Image, Text } from '@opendoor/bricks/core';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { MobileNavigationControls } from 'growth/components/welcomeToHub/MobileNavigationControls';
import { useWelcomeToHubContext } from 'growth/components/welcomeToHub/WelcomeToHubContext';
import { WelcomeToHubVariant } from 'growth/components/welcomeToHub/WelcomeToHubLoadingPage';

export const welcomeToHubSteps = [
  {
    title: 'Find peace of mind with an all-cash offer',
    image: 'https://imgdrop.imgix.net/00a0d03c127141d580534ea8a9bbaaaa.svg',
    mobile_image: 'https://imgdrop.imgix.net/af40873779714977b05a84ab918d2440.svg',
    duration: 3500,
  },
  {
    title: 'Choose when you want to sell and move ',
    image: 'https://imgdrop.imgix.net/c6eb1b9120dd42ce91b1906d8a80a7a5.svg',
    mobile_image: 'https://imgdrop.imgix.net/04d2a31fd9ef4c4ba16270da9663e357.svg',
    duration: 3500,
  },
  {
    title: 'Change your mind anytime before closing',
    image: 'https://imgdrop.imgix.net/c99d339ec12d4803ad04232db722f723.svg',
    mobile_image: 'https://imgdrop.imgix.net/9c9ca83dc2ab4b2eb628de3900e7e20c.svg',
    duration: 3500,
  },
];

export const offerRefreshSteps = [
  {
    title: 'Refresh your offer every 7 days',
    image: 'https://imgdrop.imgix.net/c6eb1b9120dd42ce91b1906d8a80a7a5.svg',
    mobile_image: 'https://imgdrop.imgix.net/04d2a31fd9ef4c4ba16270da9663e357.svg',
    duration: 3500,
  },
  {
    title: 'Stay current with local market trends',
    image: 'https://imgdrop.imgix.net/00a0d03c127141d580534ea8a9bbaaaa.svg',
    mobile_image: 'https://imgdrop.imgix.net/af40873779714977b05a84ab918d2440.svg',
    duration: 3500,
  },
  {
    title: 'Get support every step of the way',
    image: 'https://imgdrop.imgix.net/2023-03-1678390077517-49791.svg',
    mobile_image: 'https://imgdrop.imgix.net/2023-03-1678390059792-28812.svg',
    duration: 3500,
  },
];

/**
 * Returns the steps for the carousel based on the variant
 */
export const getCarouselSteps = (variant: WelcomeToHubVariant) => {
  return variant == 'offer_refresh_from_email' ? offerRefreshSteps : welcomeToHubSteps;
};

export interface ICarouselTimerData {
  stepPaused: boolean;
  setStepPaused: React.Dispatch<React.SetStateAction<boolean>>;
  stepIndex: number;
  setStepIndex: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * A carousel to be advanced through on the welcome to hub card
 *
 */
export const WelcomeToHubCarousel = () => {
  const { carouselTimerData, wthVariant } = useWelcomeToHubContext();
  const { stepPaused, setStepPaused, stepIndex, setStepIndex } = carouselTimerData;
  const { isMobile } = useMobileDevice();

  const steps = getCarouselSteps(wthVariant);

  const goToPrev = useCallback(() => {
    if (stepIndex === 0) return;
    setStepIndex((value) => {
      return value - 1;
    });
  }, [setStepIndex, stepIndex]);

  const goToNext = useCallback(() => {
    if (stepIndex === steps.length - 1) return;
    setStepIndex((value) => {
      return value + 1;
    });
  }, [setStepIndex, stepIndex, steps]);

  const handlePause = () => {
    setStepPaused(true);
  };

  const handleUnpause = () => {
    setStepPaused(false);
  };

  /* keyboard controls for desktop/tablet web */
  useEffect(() => {
    const onKeyDown = (ev: WindowEventMap['keydown']) => {
      if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        goToPrev();
      }
      if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        goToNext();
      }
      if (ev.code === 'Space') {
        ev.preventDefault();
        setStepPaused((prevStepPaused) => !prevStepPaused);
      }
    };
    addEventListener('keydown', onKeyDown);
    return () => removeEventListener('keydown', onKeyDown);
  }, [stepPaused, goToNext, goToPrev, setStepPaused]);

  return (
    <Card
      variant="primary"
      position="relative"
      width={['512px', '327px', '512px']}
      height={['488px', '364px', '488px']}
      mt={9}
      padding={0}
      role="region"
      aria-label="welcome-carousel"
      analyticsName="cake-welcome-to-hub-modal"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- fixme(@growth) */}
      <Flex
        flexDirection="column"
        width={['100%', '327px', '100%']}
        onMouseDown={handlePause}
        onMouseUp={handleUnpause}
        position="relative"
        role="group"
        aria-label={`slide ${stepIndex + 1} of ${steps.length}`}
      >
        {isMobile && (
          <MobileNavigationControls
            onGoToPrev={goToPrev}
            onGoToNext={goToNext}
            onTouchStart={handlePause}
            onTouchEnd={handleUnpause}
          />
        )}
        <Image
          key={isMobile ? steps[stepIndex].mobile_image : steps[stepIndex].image}
          alt={steps[stepIndex].title}
          src={isMobile ? steps[stepIndex].mobile_image : steps[stepIndex].image}
          width={['512px', stepIndex < 3 ? '327px' : '300px', '512px']}
          height={['488px', '364px', '488px']}
          background="linear-gradient(#E9F0FE, #E9F0FE, white)"
          borderRadius="semiRounded"
          position="absolute"
          data-testid={`${stepIndex}-image-display`}
        ></Image>
        <Box
          height={['195px', '184px', '195px']}
          width="100%"
          position="relative"
          top={['293px', '180px', '293px']}
          background="linear-gradient(180deg, #FFFFFF 47.4%, rgba(255, 255, 255, 0) 100%)"
          transform="matrix(1, 0, 0, -1, 0, 0)"
          borderRadius="semiRounded"
        />
        <Text
          width={['100%', '215px', '100%']}
          top={['225px', '100px', '225px']}
          textAlign="center"
          alignSelf="center"
          size="s2"
          fontWeight="semibold"
          id="welcome-step-title"
          position="relative"
          analyticsName={`cake-welcome-to-hub-slide-${stepIndex + 1}`}
          data-testid={`${stepIndex}-text-display`}
        >
          {steps[stepIndex].title}
        </Text>
      </Flex>
    </Card>
  );
};
